import { call, put, select, take } from "redux-saga/effects";
import { createMatchSelector } from "connected-react-router";

import * as s from "../selectors";
import pushWithLng from "./pushWithLng";
import { MODAL_TYPES } from "../constants/modals";
import * as actions from "../constants/actions";
import * as actionTypes from "../constants/actionTypes";
import firebase from "../services/firebase";
import store from "../store";
import i18n from "../i18n";

function* admin() {
  const { admin, token } = yield select(s.getUser);

  if (admin) {
    yield put(actions.fetchUserAdminResources({ token }));
  } else {
    yield put(pushWithLng("/"));
  }
}

function* account() {
  const userId = yield select(s.getUserId);
  const user = yield select(s.getUser);
  const folders = yield select(s.getFolders);
  const models = yield select(s.getModels);

  if (!user.hasVisited) {
    yield put(actions.openModal({ modalType: MODAL_TYPES.ACCOUNT_WELCOME }));
  }

  if (!folders.loaded) {
    yield put(actions.fetchFolders({ userId }));
  }

  if (!models.loaded) {
    yield put(actions.fetchModels({ userId }));
  }
}

function* modelCreate() {
  const userId = yield select(s.getUserId);
  const from = yield select(s.getRouterQueryFrom);
  const userHasVisited = yield select(s.getUserHasVisited);
  const lng = yield select(s.getLocaleLng);

  yield put(actions.clearEditor());

  // prefetch user folders
  if (userId) {
    yield put(actions.fetchFolders({ userId }));
  }

  // open welcome modal if user first visit
  if (!userHasVisited) {
    yield put(actions.openModal({ modalType: MODAL_TYPES.EDITOR_WELCOME }));
  }

  // return if from not present
  if (!from) {
    return;
  }

  yield put(actions.fetchModel({ id: from }));

  const { type } = yield take([
    actionTypes.SET_MODEL,
    actionTypes.MODEL_NOT_FOUND
  ]);

  if (type === actionTypes.MODEL_NOT_FOUND) {
    yield put(pushWithLng("/", lng));
    return;
  }

  const model = yield select(s.getModelById, from);

  if (!model) {
    yield put(pushWithLng("/create", lng));
    return;
  }

  yield put(actions.setEditorModel({ from }));
  yield put(actions.selectEditorPattern(model.patternId));
  yield put(actions.selectEditorSurface(model.surfaceId));
  yield put(
    actions.updateEditorPatternConfigs(model.patternId, model.patternConfig)
  );
}

function* modelEdit({ id }) {
  const modelsLoaded = yield select(s.getModelsLoaded);
  const lng = yield select(s.getLocaleLng);

  if (!modelsLoaded) {
    yield put(actions.fetchModels());
    yield take(actionTypes.SET_MODELS);
  }

  const model = yield select(s.getModelById, id);

  if (!model) {
    yield put(pushWithLng("/create", lng));
    return;
  }

  yield put(actions.setEditorModel({ id }));
  yield put(actions.selectEditorPattern(model.patternId));
  yield put(actions.selectEditorSurface(model.surfaceId));
  yield put(
    actions.updateEditorPatternConfigs(model.patternId, model.patternConfig)
  );
}

function* modelExport({ id }) {
  const modelExport = yield call(firebase.modelExport.fetch, { id });

  if (!modelExport) {
    // handle error
    return;
  }

  yield put(actions.setModelExport({ id, modelExport }));
}

const tasksBySelector = {
  "/admin": admin,
  "/:lang(en|es)?/account": account,
  "/:lang(en|es)?/create": modelCreate,
  "/:lang(en|es)?/models/:id/edit": modelEdit,
  "/:lang(en|es)?/models/export/:id": modelExport
};

const authRoutes = [
  "/:lang(en|es)?/account",
  "/:lang(en|es)?/models/:id/edit",
];

export function* route() {
  const locale = yield select(s.getRouterLocale);
  const prevLocale = yield select(s.getLocaleLng);

  // set locale if different
  if (locale !== prevLocale) {
    yield i18n.changeLanguage(locale);
    yield put(actions.setLocaleLng({ lng: locale }));
  }

  // get task and params
  const { task, params, authRequired } = Object
    .keys(tasksBySelector)
    .reduce((acc, selector) => {
      const matchSelector = createMatchSelector(selector);
      const state = store.getState();
      const match = matchSelector(state);

      if (match) {
        acc = {
          task: tasksBySelector[selector],
          params: match.params,
          authRequired: authRoutes.indexOf(selector) > -1
        };
      }

      return acc;
    }, {});

  // close ui sidebars if opened
  const uiSidebarsOpen = yield select(s.getUISidebarsOpen);

  if (uiSidebarsOpen) {
    yield put(actions.closeUILeftSidebar());
    yield put(actions.closeUIRightSidebar());
  }

  const authLoaded = yield select(s.getAuthLoaded);
  let authLoggedIn = yield select(s.getAuthLoggedIn);

  if (!authLoaded) {
    const { payload } = yield take(actionTypes.SET_LOADED);
    authLoggedIn = payload.loggedIn;

    if (authLoggedIn) {
      yield take(actionTypes.SET_USER);
    }
  }

  if (authRequired && !authLoggedIn) {
    yield put(pushWithLng("/", locale));
  } else if (task) {
    yield task(params);
  }
}



// WEBPACK FOOTER //
// ./src/sagas/routes.js